import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, count, deleteDoc, doc, getAggregateFromServer, getDoc, getDocs, onSnapshot, orderBy, query, limit, sum, updateDoc, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import Notification from './Notification';
import Spinner from './Spinner';
import { hasStaffFeature, isFullAdminFromStorage, isViewerFromStorage } from '../utils/staffAccess';
import { formatPaymentMethods, isConsignmentSale, isUnpaidSale } from '../utils/paymentMethods';
import { getCloudFunctionUrl } from '../config';
import InvoicePrinter from './InvoicePrinter';
import { buildQuoteInvoiceData, DEFAULT_QUOTE_CURRENCY, DEFAULT_QUOTE_DESTINATION, DEFAULT_QUOTE_PAYMENT, parseLocalDateInput, toDateInputValue } from '../utils/quoteDocument';
import './AdminDashboard.css';
import './AdminStaffUsersPage.css';
import './AdminCustomersPage.css';

/** Últimos N registros por fecha (más recientes primero). */
const RECENT_RECORDS_LIMIT = 5;
/** Lista acotada de consignaciones (el total sale de agregación, no de estos docs). */
const CONSIGNMENT_LIST_LIMIT = 80;
/** Tope al buscar en la colección (evita leer miles de documentos). */
const SEARCH_RESULTS_LIMIT = 400;
/** Tope al cargar historial de un cliente sin email/CUIT identificable. */
const CUSTOMER_ORDERS_FALLBACK_LIMIT = 150;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 2;
const DEFAULT_MONTO_MAX_SIN_CUIT = 417288;
const DEFAULT_PUNTO_VENTA = 8;

const fetchWithTimeout = (url, options = {}, timeoutMs = 120000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
};

const SOURCE_META = {
  ventas_fisicas: { label: 'Ventas físicas', feature: 'localSales', collection: 'ventas_fisicas' },
  consignacion: {
    label: 'Consignación',
    feature: 'localSales',
    collection: 'ventas_fisicas',
    consignmentOnly: true
  },
  pedidos: { label: 'Pedidos web', feature: 'adminOrders', collection: 'pedidos' },
  compras_mercadopago: { label: 'Mercado Pago', feature: 'adminSalesMp', collection: 'compras_mercadopago' },
  presupuestos: { label: 'Presupuestos', feature: 'customers', collection: 'presupuestos' }
};

function getCollectionName(sourceKey) {
  return SOURCE_META[sourceKey]?.collection || sourceKey;
}

function isPhysicalSource(sourceKey) {
  return getCollectionName(sourceKey) === 'ventas_fisicas';
}

function isQuoteSource(sourceKey) {
  return sourceKey === 'presupuestos';
}

function isNestedCustomerSource(sourceKey) {
  return isPhysicalSource(sourceKey) || isQuoteSource(sourceKey);
}

function isConsignmentTab(sourceKey) {
  return SOURCE_META[sourceKey]?.consignmentOnly === true;
}

function isCancelledSale(row) {
  return !!row && row.isConfirmed === false;
}

function isOpenConsignment(row) {
  return isConsignmentSale(row) && !isCancelledSale(row);
}

function listLimitForSource(sourceKey) {
  return isConsignmentTab(sourceKey) ? CONSIGNMENT_LIST_LIMIT : RECENT_RECORDS_LIMIT;
}

function buildSourceQuery(sourceKey, maxRecords) {
  const col = collection(db, getCollectionName(sourceKey));
  const clauses = [];
  if (isConsignmentTab(sourceKey)) {
    clauses.push(where('isConsignment', '==', true));
  }
  clauses.push(orderBy('date', 'desc'));
  if (maxRecords) clauses.push(limit(maxRecords));
  return query(col, ...clauses);
}

const emptyForm = () => ({
  name: '',
  email: '',
  phone: '',
  cuit: '',
  cuil: '',
  dni: '',
  documentType: '99',
  address: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'Argentina',
  observaciones: '',
  ivaCondition: '5',
  informacionAdicional: '',
  currency: DEFAULT_QUOTE_CURRENCY,
  destinationProvince: DEFAULT_QUOTE_DESTINATION,
  paymentTerms: DEFAULT_QUOTE_PAYMENT,
  validUntilInput: '',
  includeImages: false,
  includeDescription: false
});

function toJsDate(val) {
  if (!val) return null;
  if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  try {
    return new Date(val);
  } catch {
    return null;
  }
}

const MS_PER_DAY = 86400000;

const AGING_BUCKETS = [
  { id: 'age0', min: 0, max: 14, label: '0–14 días', hint: 'Reciente' },
  { id: 'age15', min: 15, max: 29, label: '15–29 días', hint: 'Atención' },
  { id: 'age30', min: 30, max: 59, label: '30–59 días', hint: 'Demorada' },
  { id: 'age60', min: 60, max: 89, label: '60–89 días', hint: 'Crítica' },
  { id: 'age90', min: 90, max: Infinity, label: '90+ días', hint: 'Muy vencida' }
];

function saleTimeMs(row) {
  if (!row?.date) return 0;
  if (typeof row.date.toMillis === 'function') return row.date.toMillis();
  if (typeof row.date.seconds === 'number') return row.date.seconds * 1000;
  const dt = toJsDate(row.date);
  return dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : 0;
}

function daysSinceSale(row) {
  const dt = toJsDate(row?.date);
  if (!dt || Number.isNaN(dt.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - dt.getTime()) / MS_PER_DAY));
}

function getAgingBucket(days) {
  if (days == null) return null;
  return AGING_BUCKETS.find((bucket) => days >= bucket.min && days <= bucket.max) || null;
}

function formatDaysOpen(days) {
  if (days == null) return '';
  if (days === 0) return 'Hoy';
  if (days === 1) return '1 día';
  return `${days} días`;
}

function formatMoney(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return `$${x.toFixed(2).replace('.', ',')}`;
}

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function docToForm(data, source) {
  if (isNestedCustomerSource(source)) {
    const c = data.customerData || {};
    const invEmail = data.invoiceOptions?.emailAddress || '';
    return {
      name: c.name || '',
      email: (c.email || invEmail || '').trim(),
      phone: c.phone || '',
      cuit: c.cuit || '',
      cuil: c.cuil || '',
      dni: c.dni || '',
      documentType: c.documentType || '99',
      address: c.address || '',
      city: c.city || '',
      postalCode: c.postalCode || '',
      province: c.province || '',
      country: c.country || 'Argentina',
      observaciones: c.observaciones || '',
      ivaCondition: String(c.ivaCondition || '5'),
      informacionAdicional: '',
      currency: data.currency || DEFAULT_QUOTE_CURRENCY,
      destinationProvince: data.destinationProvince || DEFAULT_QUOTE_DESTINATION,
      paymentTerms: data.paymentTerms || DEFAULT_QUOTE_PAYMENT,
      validUntilInput: toDateInputValue(data.validUntil) || '',
      includeImages: data.includeImages === true,
      includeDescription: data.includeDescription === true
    };
  }
  return {
    name: data.nombre || '',
    email: data.email || '',
    phone: data.telefono || '',
    cuit: data.cuit || '',
    cuil: data.cuil || '',
    dni: data.dni || '',
    documentType: data.documentType || (data.cuit ? '80' : '99'),
    address: data.domicilio || '',
    city: data.localidad || '',
    postalCode: data.codigoPostal || '',
    province: data.provincia || '',
    country: data.pais || 'Argentina',
    observaciones: '',
    ivaCondition: '5',
    informacionAdicional: data.informacionAdicional || '',
    currency: DEFAULT_QUOTE_CURRENCY,
    destinationProvince: DEFAULT_QUOTE_DESTINATION,
    paymentTerms: DEFAULT_QUOTE_PAYMENT,
    validUntilInput: '',
    includeImages: false,
    includeDescription: false
  };
}

function rowDisplayName(data, source) {
  if (isNestedCustomerSource(source)) {
    const n = (data.customerData && data.customerData.name) || '';
    return n.trim() || 'Consumidor final';
  }
  return (data.nombre || '').trim() || '—';
}

function canAccessSource(sourceKey) {
  const meta = SOURCE_META[sourceKey];
  if (!meta) return false;
  return isFullAdminFromStorage() || hasStaffFeature(meta.feature);
}

function customerIdentity(row, source) {
  if (!row) return { name: '', email: '', cuit: '', cuil: '', dni: '' };
  if (isNestedCustomerSource(source)) {
    return {
      name: normalizeText(row.customerData?.name || ''),
      email: normalizeText(row.customerData?.email || row.invoiceOptions?.emailAddress || ''),
      cuit: normalizeText(row.customerData?.cuit || ''),
      cuil: normalizeText(row.customerData?.cuil || ''),
      dni: normalizeText(row.customerData?.dni || '')
    };
  }
  return {
    name: normalizeText(row.nombre || ''),
    email: normalizeText(row.email || ''),
    cuit: normalizeText(row.cuit || ''),
    cuil: normalizeText(row.cuil || ''),
    dni: normalizeText(row.dni || '')
  };
}

function sameCustomer(a, b) {
  if (!a || !b) return false;
  const docKeys = ['cuit', 'cuil', 'dni'];
  for (const key of docKeys) {
    if (a[key] && b[key] && a[key] === b[key]) return true;
  }
  if (a.email && b.email && a.email === b.email) return true;
  if (a.name && b.name && a.name === b.name) return true;
  return false;
}

function getOrderStatusMeta(row, source) {
  if (!row) return { label: 'Sin estado', tone: 'neutral' };
  if (row.isActive === false) return { label: 'Archivada', tone: 'archived' };

  if (isQuoteSource(source)) {
    if (row.status === 'sent') return { label: 'Enviado', tone: 'ok' };
    return { label: 'Guardado', tone: 'pending' };
  }

  if (isPhysicalSource(source)) {
    if (row.isConfirmed === false) return { label: 'Cancelada', tone: 'danger' };
    if (isConsignmentSale(row)) {
      if (row.isPaid === true) return { label: 'Consignación cobrada', tone: 'ok' };
      return { label: 'Consignación (no cobrada)', tone: 'pending' };
    }
    if (row.isPaid === false) return { label: 'No cobrada', tone: 'pending' };
    if (row.isConfirmed === true) return { label: 'Confirmada', tone: 'ok' };
    return { label: 'Pendiente', tone: 'pending' };
  }

  if (source === 'compras_mercadopago') {
    if (row.isConfirmed === false) return { label: 'Cancelada', tone: 'danger' };
    const paymentStatus = normalizeText(row.paymentStatus || '');
    if (paymentStatus === 'approved') return { label: 'Pagada', tone: 'ok' };
    if (paymentStatus === 'rejected') return { label: 'Rechazada', tone: 'danger' };
    if (paymentStatus === 'cancelled') return { label: 'Cancelada', tone: 'danger' };
    if (paymentStatus === 'in_process') return { label: 'En proceso', tone: 'pending' };
    if (paymentStatus === 'pending') return { label: 'Pendiente pago', tone: 'pending' };
    if (row.isPaid === true) return { label: 'Pagada', tone: 'ok' };
    return { label: normalizeText(row.status || '') ? row.status : 'Procesando', tone: 'pending' };
  }

  if (source === 'pedidos') {
    if (row.isConfirmed === false) return { label: 'Cancelada', tone: 'danger' };
    const status = String(row.status || '').trim();
    if (status) return { label: status, tone: status.toLowerCase().includes('cancel') ? 'danger' : 'pending' };
    return { label: 'Pendiente', tone: 'pending' };
  }

  return { label: 'Sin estado', tone: 'neutral' };
}

const AdminCustomersPage = () => {
  const readOnly = isViewerFromStorage();
  const allowedSources = useMemo(() => {
    return Object.keys(SOURCE_META).filter(canAccessSource);
  }, []);

  const [activeSource, setActiveSource] = useState(() => allowedSources[0] || 'pedidos');
  const [items, setItems] = useState([]);
  const [searchItems, setSearchItems] = useState([]);
  const [searchCapped, setSearchCapped] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editExpanded, setEditExpanded] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingCustomerOrders, setLoadingCustomerOrders] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [issuingInvoiceId, setIssuingInvoiceId] = useState(null);
  const [montoMaxSinCuit, setMontoMaxSinCuit] = useState(DEFAULT_MONTO_MAX_SIN_CUIT);
  const [notification, setNotification] = useState({ message: '', show: false, type: 'success' });
  const [consignmentTotals, setConsignmentTotals] = useState(null);
  const [loadingConsignmentTotals, setLoadingConsignmentTotals] = useState(false);
  const [consignmentTotalsTick, setConsignmentTotalsTick] = useState(0);
  const [unpaidConsignments, setUnpaidConsignments] = useState([]);
  const [agingFilter, setAgingFilter] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [sendingQuoteId, setSendingQuoteId] = useState(null);
  const [deletingQuoteId, setDeletingQuoteId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadFacturacionConfig = async () => {
      try {
        const configSnap = await getDoc(doc(db, 'config', 'facturacion'));
        if (cancelled || !configSnap.exists() || configSnap.data().montoMaxSinCuit == null) return;
        setMontoMaxSinCuit(Number(configSnap.data().montoMaxSinCuit));
      } catch (e) {
        console.warn('Config facturación no cargada, usando umbral por defecto:', e);
      }
    };
    loadFacturacionConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  const showMessage = useCallback((message, type = 'success') => {
    setNotification({ message, show: true, type });
  }, []);

  useEffect(() => {
    if (allowedSources.length && !allowedSources.includes(activeSource)) {
      setActiveSource(allowedSources[0]);
      setSelectedId(null);
      setForm(emptyForm());
      setEditExpanded(false);
      setCustomerOrders([]);
      setAgingFilter(null);
    }
  }, [allowedSources, activeSource]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!allowedSources.includes(activeSource)) return undefined;
    setLoadingList(true);
    setSearchItems([]);
    setSearchCapped(false);
    setLoadingSearch(false);
    setSelectedId(null);
    setForm(emptyForm());
    setEditExpanded(false);
    setCustomerOrders([]);
    setAgingFilter(null);
    const q = buildSourceQuery(activeSource, listLimitForSource(activeSource));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingList(false);
      },
      (err) => {
        console.error('AdminCustomersPage snapshot:', err);
        setLoadingList(false);
        showMessage('No se pudo cargar la lista. Revisá permisos o conexión.', 'error');
      }
    );
    return () => unsub();
  }, [activeSource, allowedSources, showMessage]);

  useEffect(() => {
    if (!isConsignmentTab(activeSource) || !allowedSources.includes(activeSource)) {
      setUnpaidConsignments([]);
      return undefined;
    }
    const unpaidQuery = query(
      collection(db, 'ventas_fisicas'),
      where('isConsignment', '==', true),
      where('isPaid', '==', false)
    );
    const unsub = onSnapshot(
      unpaidQuery,
      (snap) => {
        setUnpaidConsignments(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((row) => !isCancelledSale(row))
        );
      },
      (err) => {
        console.error('AdminCustomersPage unpaid consignments:', err);
        setUnpaidConsignments([]);
      }
    );
    return () => unsub();
  }, [activeSource, allowedSources]);

  const normalizedTerm = useMemo(() => normalizeText(debouncedSearch), [debouncedSearch]);
  const searchActive = normalizedTerm.length >= MIN_SEARCH_CHARS;

  useEffect(() => {
    if (!allowedSources.includes(activeSource)) return undefined;
    if (!searchActive) {
      setSearchItems([]);
      setSearchCapped(false);
      setLoadingSearch(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingSearch(true);
    const q = buildSourceQuery(activeSource, SEARCH_RESULTS_LIMIT);

    const runSearch = async () => {
      try {
        const snap = await getDocs(q);
        if (cancelled) return;
        setSearchItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSearchCapped(snap.size >= SEARCH_RESULTS_LIMIT);
      } catch (error) {
        console.error('AdminCustomersPage search:', error);
        if (!cancelled) {
          showMessage('No se pudo buscar en la colección.', 'error');
          setSearchItems([]);
          setSearchCapped(false);
        }
      } finally {
        if (!cancelled) setLoadingSearch(false);
      }
    };

    runSearch();
    return () => {
      cancelled = true;
    };
  }, [activeSource, allowedSources, searchActive, showMessage]);

  const sourceItems = useMemo(() => {
    if (isConsignmentTab(activeSource) && agingFilter) {
      return unpaidConsignments.length
        ? unpaidConsignments
        : items.filter((row) => isOpenConsignment(row) && isUnpaidSale(row));
    }
    return searchActive ? searchItems : items;
  }, [activeSource, agingFilter, unpaidConsignments, searchActive, searchItems, items]);

  const matchesCustomerSearch = useCallback(
    (row) => {
      const name = normalizeText(rowDisplayName(row, activeSource));
      const on = normalizeText(row.orderNumber || '');
      const email =
        isNestedCustomerSource(activeSource)
          ? normalizeText(row.customerData?.email || row.invoiceOptions?.emailAddress || '')
          : normalizeText(row.email || '');
      const cuit =
        isNestedCustomerSource(activeSource)
          ? normalizeText(row.customerData?.cuit || '')
          : normalizeText(row.cuit || '');
      const cuil =
        isNestedCustomerSource(activeSource)
          ? normalizeText(row.customerData?.cuil || '')
          : normalizeText(row.cuil || '');
      const dni =
        isNestedCustomerSource(activeSource)
          ? normalizeText(row.customerData?.dni || '')
          : normalizeText(row.dni || '');
      const phone = isNestedCustomerSource(activeSource) ? '' : normalizeText(row.telefono || '');
      const address =
        isNestedCustomerSource(activeSource)
          ? normalizeText(row.customerData?.address || '')
          : normalizeText(row.domicilio || '');
      return (
        name.includes(normalizedTerm) ||
        on.includes(normalizedTerm) ||
        email.includes(normalizedTerm) ||
        cuit.includes(normalizedTerm) ||
        cuil.includes(normalizedTerm) ||
        dni.includes(normalizedTerm) ||
        phone.includes(normalizedTerm) ||
        address.includes(normalizedTerm) ||
        normalizeText(row.id).includes(normalizedTerm)
      );
    },
    [activeSource, normalizedTerm]
  );

  const filteredItems = useMemo(() => {
    let rows = sourceItems;
    if (isConsignmentTab(activeSource)) {
      rows = rows.filter((row) => !isCancelledSale(row));
    }
    if (isConsignmentTab(activeSource) && agingFilter) {
      rows = rows.filter((row) => getAgingBucket(daysSinceSale(row))?.id === agingFilter);
    }
    if (searchActive) {
      rows = rows.filter(matchesCustomerSearch);
    }
    if (isConsignmentTab(activeSource) && agingFilter) {
      return [...rows].sort((a, b) => saleTimeMs(b) - saleTimeMs(a));
    }
    return rows;
  }, [sourceItems, searchActive, activeSource, agingFilter, matchesCustomerSearch]);

  const selectedRow = useMemo(
    () =>
      selectedId
        ? sourceItems.find((r) => r.id === selectedId) ||
          items.find((r) => r.id === selectedId) ||
          unpaidConsignments.find((r) => r.id === selectedId)
        : null,
    [sourceItems, items, unpaidConsignments, selectedId]
  );

  const consignmentAging = useMemo(() => {
    const pool = unpaidConsignments.length
      ? unpaidConsignments
      : items.filter((row) => isOpenConsignment(row) && isUnpaidSale(row));
    const buckets = AGING_BUCKETS.map((bucket) => ({ ...bucket, count: 0, amount: 0 }));
    pool.forEach((row) => {
      const bucket = getAgingBucket(daysSinceSale(row));
      if (!bucket) return;
      const slot = buckets.find((item) => item.id === bucket.id);
      if (!slot) return;
      slot.count += 1;
      slot.amount += Number(row.totalPrice) || 0;
    });
    return {
      buckets,
      fromVisibleList: unpaidConsignments.length === 0,
      unpaidCount: pool.length,
      unpaidAmount: pool.reduce((acc, row) => acc + (Number(row.totalPrice) || 0), 0)
    };
  }, [unpaidConsignments, items]);

  const consignmentHeadlineTotals = useMemo(() => {
    const unpaidCount = consignmentAging.unpaidCount;
    const unpaidAmount = consignmentAging.unpaidAmount;
    if (!consignmentTotals) {
      return {
        count: unpaidCount,
        amount: unpaidAmount,
        paidCount: 0,
        paidAmount: 0,
        unpaidCount,
        unpaidAmount
      };
    }
    return {
      count: consignmentTotals.paidCount + unpaidCount,
      amount: consignmentTotals.paidAmount + unpaidAmount,
      paidCount: consignmentTotals.paidCount,
      paidAmount: consignmentTotals.paidAmount,
      unpaidCount,
      unpaidAmount
    };
  }, [consignmentTotals, consignmentAging]);

  const openRow = (row) => {
    setSelectedId(row.id);
    setForm(docToForm(row, activeSource));
    setEditExpanded(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  useEffect(() => {
    let cancelled = false;
    const loadCustomerOrders = async () => {
      if (!selectedRow || !allowedSources.includes(activeSource)) {
        setCustomerOrders([]);
        setLoadingCustomerOrders(false);
        return;
      }
      setLoadingCustomerOrders(true);
      try {
        const targetIdentity = customerIdentity(selectedRow, activeSource);
        const col = collection(db, getCollectionName(activeSource));
        const byId = new Map();
        const queries = [];

        if (isNestedCustomerSource(activeSource) && !isQuoteSource(activeSource)) {
          const rawEmail = (
            selectedRow.customerData?.email ||
            selectedRow.invoiceOptions?.emailAddress ||
            ''
          ).trim();
          const rawCuit = (selectedRow.customerData?.cuit || '').trim();
          const rawDni = (selectedRow.customerData?.dni || '').trim();
          if (rawEmail) {
            queries.push(
              query(
                col,
                where('customerData.email', '==', rawEmail),
                orderBy('date', 'desc'),
                limit(CUSTOMER_ORDERS_FALLBACK_LIMIT)
              )
            );
          }
          if (rawCuit) {
            queries.push(
              query(
                col,
                where('customerData.cuit', '==', rawCuit),
                orderBy('date', 'desc'),
                limit(CUSTOMER_ORDERS_FALLBACK_LIMIT)
              )
            );
          }
          if (rawDni) {
            queries.push(
              query(
                col,
                where('customerData.dni', '==', rawDni),
                orderBy('date', 'desc'),
                limit(CUSTOMER_ORDERS_FALLBACK_LIMIT)
              )
            );
          }
        } else {
          const rawEmail = (selectedRow.email || '').trim();
          const rawCuit = (selectedRow.cuit || '').trim();
          const rawCuil = (selectedRow.cuil || '').trim();
          const rawDni = (selectedRow.dni || '').trim();
          if (rawEmail) {
            queries.push(
              query(col, where('email', '==', rawEmail), orderBy('date', 'desc'), limit(CUSTOMER_ORDERS_FALLBACK_LIMIT))
            );
          }
          if (rawCuit) {
            queries.push(
              query(col, where('cuit', '==', rawCuit), orderBy('date', 'desc'), limit(CUSTOMER_ORDERS_FALLBACK_LIMIT))
            );
          }
          if (rawCuil) {
            queries.push(
              query(col, where('cuil', '==', rawCuil), orderBy('date', 'desc'), limit(CUSTOMER_ORDERS_FALLBACK_LIMIT))
            );
          }
          if (rawDni) {
            queries.push(
              query(col, where('dni', '==', rawDni), orderBy('date', 'desc'), limit(CUSTOMER_ORDERS_FALLBACK_LIMIT))
            );
          }
        }

        if (queries.length > 0) {
          const snaps = await Promise.all(queries.map((q) => getDocs(q)));
          if (cancelled) return;
          snaps.forEach((snap) => {
            snap.docs.forEach((d) => {
              const row = { id: d.id, ...d.data() };
              if (sameCustomer(targetIdentity, customerIdentity(row, activeSource))) {
                byId.set(d.id, row);
              }
            });
          });
        } else {
          const snap = await getDocs(
            query(col, orderBy('date', 'desc'), limit(CUSTOMER_ORDERS_FALLBACK_LIMIT))
          );
          if (cancelled) return;
          snap.docs.forEach((d) => {
            const row = { id: d.id, ...d.data() };
            if (sameCustomer(targetIdentity, customerIdentity(row, activeSource))) {
              byId.set(d.id, row);
            }
          });
        }

        const matches = Array.from(byId.values())
          .filter((row) => !isConsignmentTab(activeSource) || isOpenConsignment(row))
          .sort((a, b) => {
            const ta = a.date?.toMillis?.() ?? a.date?.seconds * 1000 ?? 0;
            const tb = b.date?.toMillis?.() ?? b.date?.seconds * 1000 ?? 0;
            return tb - ta;
          });
        setCustomerOrders(matches);
      } catch (error) {
        console.error('Error cargando órdenes del cliente:', error);
        if (!cancelled) setCustomerOrders([]);
      } finally {
        if (!cancelled) setLoadingCustomerOrders(false);
      }
    };
    loadCustomerOrders();
    return () => {
      cancelled = true;
    };
  }, [selectedRow, activeSource, allowedSources]);

  const customerConsignmentSummary = useMemo(() => {
    if (!isPhysicalSource(activeSource)) return null;
    const rows = customerOrders.length ? customerOrders : (selectedRow ? [selectedRow] : []);
    const pending = rows.filter((r) => isOpenConsignment(r) && isUnpaidSale(r));
    if (!pending.length) return null;
    const amount = pending.reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);
    return { count: pending.length, amount };
  }, [activeSource, customerOrders, selectedRow]);

  useEffect(() => {
    if (!isConsignmentTab(activeSource) || !allowedSources.includes(activeSource)) {
      setConsignmentTotals(null);
      setLoadingConsignmentTotals(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingConsignmentTotals(true);
    const col = collection(db, 'ventas_fisicas');
    const allQ = query(col, where('isConsignment', '==', true));
    const paidQ = query(col, where('isConsignment', '==', true), where('isPaid', '==', true));

    const loadTotals = async () => {
      try {
        const [allSnap, paidSnap] = await Promise.all([
          getAggregateFromServer(allQ, { amount: sum('totalPrice'), qty: count() }),
          getAggregateFromServer(paidQ, { amount: sum('totalPrice'), qty: count() })
        ]);
        if (cancelled) return;
        const countAll = Number(allSnap.data().qty) || 0;
        const amountAll = Number(allSnap.data().amount) || 0;
        const countPaid = Number(paidSnap.data().qty) || 0;
        const amountPaid = Number(paidSnap.data().amount) || 0;
        setConsignmentTotals({
          count: countAll,
          amount: amountAll,
          paidCount: countPaid,
          paidAmount: amountPaid,
          unpaidCount: Math.max(0, countAll - countPaid),
          unpaidAmount: Math.max(0, amountAll - amountPaid)
        });
      } catch (error) {
        console.error('AdminCustomersPage consignment totals:', error);
        if (!cancelled) setConsignmentTotals(null);
      } finally {
        if (!cancelled) setLoadingConsignmentTotals(false);
      }
    };

    loadTotals();
    return () => {
      cancelled = true;
    };
  }, [activeSource, allowedSources, consignmentTotalsTick]);

  const handleSave = async () => {
    if (readOnly || !selectedId) return;
    const row =
      selectedRow ||
      sourceItems.find((r) => r.id === selectedId) ||
      items.find((r) => r.id === selectedId) ||
      searchItems.find((r) => r.id === selectedId);
    if (!row) {
      showMessage('El registro ya no está en la lista. Actualizá e intentá de nuevo.', 'error');
      return;
    }
    setSaving(true);
    try {
      const ref = doc(db, getCollectionName(activeSource), selectedId);
      if (isNestedCustomerSource(activeSource)) {
        const prev = row.customerData || {};
        const customerData = {
          ...prev,
          name: (form.name || '').trim() || 'Consumidor Final',
          email: (form.email || '').trim(),
          phone: (form.phone || '').trim(),
          cuit: (form.cuit || '').trim(),
          cuil: (form.cuil || '').trim(),
          dni: (form.dni || '').trim(),
          documentType: form.documentType,
          address: (form.address || '').trim(),
          city: (form.city || '').trim(),
          postalCode: (form.postalCode || '').trim(),
          province: (form.province || '').trim(),
          country: (form.country || '').trim() || 'Argentina',
          observaciones: (form.observaciones || '').trim(),
          ivaCondition: form.ivaCondition
        };
        const patch = { customerData };
        if (row.invoiceOptions && typeof row.invoiceOptions === 'object') {
          patch.invoiceOptions = {
            ...row.invoiceOptions,
            emailAddress: (form.email || '').trim()
          };
        }
        if (isQuoteSource(activeSource)) {
          const validUntil = parseLocalDateInput(form.validUntilInput);
          patch.currency = (form.currency || '').trim() || DEFAULT_QUOTE_CURRENCY;
          patch.destinationProvince = (form.destinationProvince || '').trim() || DEFAULT_QUOTE_DESTINATION;
          patch.paymentTerms = (form.paymentTerms || '').trim() || DEFAULT_QUOTE_PAYMENT;
          patch.includeImages = form.includeImages === true;
          patch.includeDescription = form.includeDescription === true;
          if (validUntil) patch.validUntil = validUntil;
        }
        await updateDoc(ref, patch);
      } else {
        await updateDoc(ref, {
          nombre: (form.name || '').trim(),
          telefono: (form.phone || '').trim(),
          email: (form.email || '').trim(),
          domicilio: (form.address || '').trim(),
          localidad: (form.city || '').trim(),
          codigoPostal: (form.postalCode || '').trim(),
          provincia: (form.province || '').trim(),
          pais: (form.country || '').trim() || 'Argentina',
          cuit: (form.cuit || '').trim() || null,
          cuil: (form.cuil || '').trim() || null,
          dni: (form.dni || '').trim() || null,
          documentType: form.documentType,
          informacionAdicional: (form.informacionAdicional || '').trim()
        });
      }
      showMessage('Datos del cliente guardados.', 'success');
      setEditExpanded(false);
    } catch (e) {
      console.error(e);
      showMessage('No se pudo guardar. Revisá permisos en Firestore.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const patchLocalSale = useCallback((saleId, patch) => {
    const apply = (rows) => rows.map((row) => (row.id === saleId ? { ...row, ...patch } : row));
    setCustomerOrders((prev) => apply(prev));
    setItems((prev) => apply(prev));
    setSearchItems((prev) => apply(prev));
  }, []);

  const resolveOrderEmail = useCallback((order) => {
    const fromForm = selectedId === order.id ? (form.email || '').trim() : '';
    const email = (
      fromForm ||
      order.customerData?.email ||
      order.invoiceOptions?.emailAddress ||
      form.email ||
      ''
    ).trim();
    return email.includes('@') ? email : '';
  }, [form.email, selectedId]);

  const handleIssueConsignmentInvoice = async (order) => {
    if (readOnly || !isPhysicalSource(activeSource) || !isConsignmentSale(order)) return;

    const emailToSend = resolveOrderEmail(order);
    if (!emailToSend) {
      showMessage('Cargá y guardá el email del cliente antes de emitir o enviar la factura.', 'error');
      return;
    }

    if (order.invoiceData) {
      let url = '';
      try {
        url = getCloudFunctionUrl('sendOrderInvoiceByEmail');
      } catch {
        url = '';
      }
      if (!url) {
        showMessage('URL de envío de factura no configurada.', 'error');
        return;
      }
      setIssuingInvoiceId(order.id);
      try {
        const res = await fetchWithTimeout(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            collection: 'ventas_fisicas',
            docId: order.id,
            email: emailToSend
          })
        }, 60000);
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result.success === false) {
          throw new Error(result.message || 'No se pudo enviar la factura');
        }
        showMessage(`Factura enviada a ${emailToSend}`, 'success');
      } catch (e) {
        console.error(e);
        showMessage(e.message || 'Error al enviar la factura', 'error');
      } finally {
        setIssuingInvoiceId(null);
      }
      return;
    }

    const totalPrice = Number(order.totalPrice) || 0;
    const cuit = (order.customerData?.cuit || form.cuit || '').trim();
    const dni = (order.customerData?.dni || form.dni || '').trim();
    const fiscalDigits = (cuit || dni).replace(/\D/g, '');
    if (totalPrice >= montoMaxSinCuit && fiscalDigits.length < 8) {
      showMessage(
        `Para facturar operaciones de ${formatMoney(montoMaxSinCuit)} o más AFIP exige CUIT o DNI. Completá el dato fiscal del cliente y guardá los cambios.`,
        'error'
      );
      return;
    }

    let url = '';
    try {
      url = getCloudFunctionUrl('generateAfipInvoiceReal');
    } catch {
      url = '';
    }
    if (!url) {
      showMessage('URL de facturación AFIP no configurada.', 'error');
      return;
    }

    const pv = Number(order.invoiceOptions?.puntoVenta) || DEFAULT_PUNTO_VENTA;
    const customer = {
      name: order.customerData?.name || form.name || 'Consumidor Final',
      email: emailToSend,
      cuit: cuit || order.customerData?.cuit || '',
      cuil: order.customerData?.cuil || cuit || '',
      dni: dni || order.customerData?.dni || '',
      documentType: order.customerData?.documentType || (cuit ? '80' : dni ? '96' : '99'),
      address: order.customerData?.address || form.address || '',
      city: order.customerData?.city || form.city || '',
      postalCode: order.customerData?.postalCode || form.postalCode || '',
      province: order.customerData?.province || form.province || '',
      country: order.customerData?.country || form.country || 'Argentina'
    };

    setIssuingInvoiceId(order.id);
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          saleData: {
            orderNumber: Number(order.orderNumber),
            products: (order.products || []).map((p) => ({
              id: p.id,
              model: p.model,
              name: p.model || p.name,
              price: Number(p.price) || 0,
              quantity: Number(p.quantity) || 0
            })),
            customerData: customer,
            subtotal: Number(order.subtotal) || 0,
            discount: order.discount != null ? Number(order.discount) : 0,
            discountType: order.discountType || 'percentage',
            discountAmount: Number(order.discountAmount) || 0,
            totalPrice,
            invoiceOptions: { sendByEmail: true, emailAddress: emailToSend }
          },
          invoiceType: 'facturaC',
          puntoVenta: pv,
          sendByEmail: true,
          emailAddress: emailToSend
        })
      }, 120000);

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success || !result.data?.invoice) {
        throw new Error(result.message || result.error || `No se pudo emitir la factura (HTTP ${response.status})`);
      }

      const invoiceData = { ...result.data.invoice, id: order.id };
      const invoiceOptions = {
        ...(order.invoiceOptions || {}),
        generateInvoice: true,
        sendByEmail: true,
        emailAddress: emailToSend,
        invoiceType: 'facturaC',
        puntoVenta: pv
      };
      await updateDoc(doc(db, 'ventas_fisicas', order.id), {
        invoiceData,
        invoiceOptions,
        customerData: {
          ...(order.customerData || {}),
          email: emailToSend
        }
      });
      patchLocalSale(order.id, { invoiceData, invoiceOptions, customerData: { ...(order.customerData || {}), email: emailToSend } });

      if (result.data?.emailSent) {
        showMessage(`Factura emitida y enviada a ${emailToSend}`, 'success');
      } else if (result.data?.emailError) {
        showMessage(`Factura emitida, pero el email no se envió: ${result.data.emailError}`, 'warning');
      } else {
        showMessage(`Factura emitida. No se confirmó el envío a ${emailToSend}.`, 'warning');
      }
    } catch (e) {
      console.error(e);
      showMessage(e.message || 'Error al emitir la factura AFIP', 'error');
    } finally {
      setIssuingInvoiceId(null);
    }
  };

  const handleSendQuoteEmail = async (quote) => {
    if (readOnly || !isQuoteSource(activeSource) || !quote?.id) return;
    const emailTo = resolveOrderEmail(quote) || (quote.customerData?.email || quote.invoiceOptions?.emailAddress || '').trim();
    if (!emailTo.includes('@')) {
      showMessage('Cargá y guardá el email del cliente antes de enviar el presupuesto.', 'error');
      return;
    }
    let url = '';
    try {
      url = getCloudFunctionUrl('sendLocalSaleQuoteEmail');
    } catch {
      url = '';
    }
    if (!url) {
      showMessage('URL de envío de presupuesto no configurada.', 'error');
      return;
    }
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      showMessage('Iniciá sesión para enviar el presupuesto por email.', 'error');
      return;
    }
    setSendingQuoteId(quote.id);
    try {
      const res = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ quoteId: quote.id, email: emailTo })
      }, 90000);
      const result = await res.json().catch(() => ({}));
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'No se pudo enviar el presupuesto.');
      }
      patchLocalSale(quote.id, { status: 'sent', emailedTo: emailTo });
      showMessage(`Presupuesto enviado a ${emailTo}`, 'success');
    } catch (e) {
      console.error(e);
      showMessage(e.message || 'Error al enviar el presupuesto', 'error');
    } finally {
      setSendingQuoteId(null);
    }
  };

  const handleDeleteQuote = async (quote) => {
    if (readOnly || !isQuoteSource(activeSource) || !quote?.id) return;
    const quoteLabel = quote.quoteNumber || quote.orderNumber || quote.id;
    const clientName = (quote.customerData?.name || form.name || '').trim() || 'cliente';
    const confirmed = window.confirm(
      `¿Eliminar el presupuesto #${quoteLabel} de ${clientName}?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    setDeletingQuoteId(quote.id);
    try {
      await deleteDoc(doc(db, 'presupuestos', quote.id));
      const drop = (rows) => rows.filter((row) => row.id !== quote.id);
      setItems((prev) => drop(prev));
      setSearchItems((prev) => drop(prev));
      setCustomerOrders((prev) => drop(prev));
      if (selectedId === quote.id) {
        setSelectedId(null);
        setForm(emptyForm());
        setEditExpanded(false);
      }
      if (previewQuote?.id === quote.id) setPreviewQuote(null);
      showMessage(`Presupuesto #${quoteLabel} eliminado.`, 'success');
    } catch (e) {
      console.error(e);
      showMessage('No se pudo eliminar el presupuesto. Revisá permisos en Firestore.', 'error');
    } finally {
      setDeletingQuoteId(null);
    }
  };

  const handleToggleConsignmentPaid = async (order) => {
    if (readOnly || !isPhysicalSource(activeSource) || !isConsignmentSale(order)) return;
    const markPaid = isUnpaidSale(order);
    setMarkingPaidId(order.id);
    try {
      const ref = doc(db, 'ventas_fisicas', order.id);
      await updateDoc(ref, {
        isPaid: markPaid,
        paidAt: markPaid ? new Date() : null
      });
      patchLocalSale(order.id, { isPaid: markPaid, paidAt: markPaid ? new Date() : null });
      setConsignmentTotalsTick((n) => n + 1);
      showMessage(
        markPaid
          ? `Consignación #${order.orderNumber || ''} marcada como pagada.`
          : `Consignación #${order.orderNumber || ''} marcada como no cobrada.`,
        'success'
      );
    } catch (e) {
      console.error(e);
      showMessage('No se pudo actualizar el cobro. Revisá permisos en Firestore.', 'error');
    } finally {
      setMarkingPaidId(null);
    }
  };

  if (!allowedSources.length) {
    return (
      <div className="admin-dashboard admin-staff-page admin-customers-page">
        <div className="admin-customers-page__toolbar">
          <Link to="/admin" className="admin-staff-page__back">
            ← Panel
          </Link>
        </div>
        <p className="admin-customers-page__empty">No tenés permisos para gestionar clientes de ventas.</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard admin-staff-page admin-customers-page">
      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={() => setNotification((p) => ({ ...p, show: false }))}
      />

      <div className="admin-customers-page__toolbar">
        <h1>Clientes y datos de contacto</h1>
        <Link to="/admin" className="admin-staff-page__back">
          ← Volver al panel
        </Link>
      </div>

      {readOnly && (
        <div className="admin-customers-page__banner" role="status">
          Modo solo lectura: no podés guardar cambios.
        </div>
      )}

      <div className="admin-customers-page__tabs" role="tablist" aria-label="Origen de la venta">
        {allowedSources.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeSource === key}
            className={`admin-customers-page__tab ${key === 'consignacion' ? 'is-consignment' : ''} ${key === 'presupuestos' ? 'is-quote' : ''} ${activeSource === key ? 'is-active' : ''}`}
            onClick={() => setActiveSource(key)}
          >
            {SOURCE_META[key].label}
          </button>
        ))}
      </div>

      {isConsignmentTab(activeSource) && (
        <div className="admin-customers-page__totals" role="status">
          {loadingConsignmentTotals && !consignmentTotals ? (
            <p className="admin-customers-page__muted">Calculando totales de consignación…</p>
          ) : (
            <>
              <div className="admin-customers-page__totals-item">
                <span className="admin-customers-page__totals-label">Consignaciones</span>
                <strong>{consignmentHeadlineTotals.count}</strong>
              </div>
              <div className="admin-customers-page__totals-item">
                <span className="admin-customers-page__totals-label">Total consignado</span>
                <strong>{formatMoney(consignmentHeadlineTotals.amount)}</strong>
              </div>
              <div className="admin-customers-page__totals-item is-pending">
                <span className="admin-customers-page__totals-label">No cobrado</span>
                <strong>
                  {formatMoney(consignmentHeadlineTotals.unpaidAmount)}
                  <span className="admin-customers-page__totals-count">
                    ({consignmentHeadlineTotals.unpaidCount})
                  </span>
                </strong>
              </div>
              <div className="admin-customers-page__totals-item is-ok">
                <span className="admin-customers-page__totals-label">Cobrado</span>
                <strong>
                  {formatMoney(consignmentHeadlineTotals.paidAmount)}
                  <span className="admin-customers-page__totals-count">
                    ({consignmentHeadlineTotals.paidCount})
                  </span>
                </strong>
              </div>
            </>
          )}
        </div>
      )}

      {isConsignmentTab(activeSource) && (
        <section className="admin-customers-page__aging" aria-label="Antigüedad de consignaciones no cobradas">
          <div className="admin-customers-page__aging-head">
            <div>
              <h2>Antigüedad de lo no cobrado</h2>
              <p>
                Días desde la entrega. Tocá un tramo para ver solo esas consignaciones.
                {consignmentAging.fromVisibleList ? ' (según la lista cargada)' : ''}
              </p>
            </div>
            {agingFilter ? (
              <button
                type="button"
                className="admin-customers-page__btn admin-customers-page__btn-secondary"
                onClick={() => setAgingFilter(null)}
              >
                Ver todas
              </button>
            ) : null}
          </div>
          <div className="admin-customers-page__aging-grid">
            {consignmentAging.buckets.map((bucket) => (
              <button
                key={bucket.id}
                type="button"
                className={`admin-customers-page__aging-card is-${bucket.id} ${agingFilter === bucket.id ? 'is-active' : ''}`}
                onClick={() => setAgingFilter((prev) => (prev === bucket.id ? null : bucket.id))}
                aria-pressed={agingFilter === bucket.id}
              >
                <span className="admin-customers-page__aging-label">{bucket.label}</span>
                <span className="admin-customers-page__aging-hint">{bucket.hint}</span>
                <strong>{formatMoney(bucket.amount)}</strong>
                <span className="admin-customers-page__aging-count">
                  {bucket.count} {bucket.count === 1 ? 'consignación' : 'consignaciones'}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="admin-customers-page__layout">
        <section className="admin-customers-page__list-panel admin-staff-page__form">
          <div className="admin-customers-page__list-head">
            <h2>
              {searchActive
                ? searchCapped
                  ? `Resultados (últimos ${SEARCH_RESULTS_LIMIT} registros)`
                  : 'Resultados de búsqueda'
                : search.trim()
                  ? `Ingresá al menos ${MIN_SEARCH_CHARS} caracteres`
                  : isConsignmentTab(activeSource)
                    ? agingFilter
                      ? AGING_BUCKETS.find((bucket) => bucket.id === agingFilter)?.label || 'Tramo de antigüedad'
                      : consignmentHeadlineTotals.count > filteredItems.length
                      ? `Mostrando ${filteredItems.length} de ${consignmentHeadlineTotals.count}`
                      : 'Consignaciones recientes'
                  : isQuoteSource(activeSource)
                    ? 'Presupuestos recientes'
                    : 'Últimos 5 registros'}
            </h2>
            <input
              type="search"
              className="admin-customers-page__search"
              placeholder="Buscar por nombre, orden, email, CUIT…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Filtrar lista"
            />
          </div>
          {loadingList || (searchActive && loadingSearch) ? (
            <div className="admin-customers-page__spinner-wrap">
              <Spinner />
            </div>
          ) : filteredItems.length === 0 ? (
            <p className="admin-customers-page__muted">
              {isConsignmentTab(activeSource)
                ? agingFilter
                  ? 'No hay consignaciones no cobradas en este tramo.'
                  : 'No hay ventas en consignación.'
                : isQuoteSource(activeSource)
                  ? 'No hay presupuestos para mostrar.'
                : 'No hay registros para mostrar.'}
            </p>
          ) : (
            <ul className="admin-customers-page__list">
              {filteredItems.map((row) => {
                const dt = toJsDate(row.date);
                const label = rowDisplayName(row, activeSource);
                const statusMeta = getOrderStatusMeta(row, activeSource);
                const daysOpen = isConsignmentTab(activeSource) ? daysSinceSale(row) : null;
                const ageBucket = daysOpen != null && isUnpaidSale(row) ? getAgingBucket(daysOpen) : null;
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      className={`admin-customers-page__list-item ${selectedId === row.id ? 'is-selected' : ''} ${ageBucket ? `is-${ageBucket.id}` : ''}`}
                      onClick={() => openRow(row)}
                    >
                      <span className="admin-customers-page__list-title">{label}</span>
                      <span className="admin-customers-page__list-meta">
                        #{row.orderNumber || '—'} · {dt ? dt.toLocaleString('es-AR') : '—'} ·{' '}
                        {formatMoney(row.totalPrice)}
                      </span>
                      <span className="admin-customers-page__list-badges">
                        {(isQuoteSource(activeSource) || (isPhysicalSource(activeSource) && (isConsignmentSale(row) || isUnpaidSale(row)))) && (
                          <span className={`admin-customers-page__status-badge is-${statusMeta.tone}`}>
                            {statusMeta.label}
                          </span>
                        )}
                        {isConsignmentTab(activeSource) && daysOpen != null && (
                          <span
                            className={`admin-customers-page__status-badge ${ageBucket ? `is-${ageBucket.id}` : 'is-ok'}`}
                          >
                            {isUnpaidSale(row) ? formatDaysOpen(daysOpen) : `Cobrada · ${formatDaysOpen(daysOpen)}`}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="admin-customers-page__form-panel admin-staff-page__form">
          <h2>Datos del cliente</h2>
          {!selectedRow ? (
            <p className="admin-customers-page__muted">Elegí un registro de la lista para editar.</p>
          ) : (
            <>
              <p className="admin-customers-page__selection-hint">
                Editando {SOURCE_META[activeSource].label} · {isQuoteSource(activeSource) ? 'Presupuesto' : 'Orden'} #{selectedRow.orderNumber || selectedRow.quoteNumber || '—'}
              </p>
              {isQuoteSource(activeSource) ? (
                <>
                <div className="admin-customers-page__quote-actions">
                  <button
                    type="button"
                    className="admin-customers-page__btn admin-customers-page__btn-secondary admin-customers-page__btn-quote"
                    onClick={() => setPreviewQuote({
                      ...selectedRow,
                      currency: form.currency,
                      destinationProvince: form.destinationProvince,
                      paymentTerms: form.paymentTerms,
                      validUntil: parseLocalDateInput(form.validUntilInput) || selectedRow.validUntil,
                      includeImages: form.includeImages === true,
                      includeDescription: form.includeDescription === true
                    })}
                  >
                    Ver presupuesto
                  </button>
                  {!readOnly ? (
                    <>
                    <button
                      type="button"
                      className="admin-customers-page__btn admin-customers-page__btn-invoice admin-customers-page__btn-quote"
                      onClick={() => handleSendQuoteEmail(selectedRow)}
                      disabled={sendingQuoteId === selectedRow.id}
                    >
                      {sendingQuoteId === selectedRow.id ? 'Enviando…' : 'Enviar por email'}
                    </button>
                    <button
                      type="button"
                      className="admin-customers-page__btn admin-customers-page__btn-danger admin-customers-page__btn-quote"
                      onClick={() => handleDeleteQuote(selectedRow)}
                      disabled={deletingQuoteId === selectedRow.id || sendingQuoteId === selectedRow.id}
                    >
                      {deletingQuoteId === selectedRow.id ? 'Eliminando…' : 'Eliminar'}
                    </button>
                    </>
                  ) : null}
                </div>
                <div className="admin-customers-page__quote-terms">
                  <h3>Condiciones del presupuesto</h3>
                  <div className="admin-staff-page__fields admin-customers-page__fields">
                    <label>
                      Moneda
                      <input name="currency" value={form.currency} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      Prov. Destino
                      <input name="destinationProvince" value={form.destinationProvince} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      Válido hasta
                      <input
                        type="date"
                        name="validUntilInput"
                        value={form.validUntilInput}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      Forma de pago
                      <input name="paymentTerms" value={form.paymentTerms} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label className="admin-customers-page__check-label">
                      <input
                        type="checkbox"
                        name="includeImages"
                        checked={form.includeImages === true}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      />
                      Incluir imágenes de producto
                    </label>
                    <label className="admin-customers-page__check-label">
                      <input
                        type="checkbox"
                        name="includeDescription"
                        checked={form.includeDescription === true}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      />
                      Incluir descripción de producto
                    </label>
                  </div>
                  {!readOnly ? (
                    <button
                      type="button"
                      className="admin-customers-page__btn admin-customers-page__btn-primary admin-customers-page__btn-quote"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Guardando…' : 'Guardar condiciones'}
                    </button>
                  ) : null}
                </div>
              </>
              ) : null}
              {customerConsignmentSummary && (
                <div className="admin-customers-page__consignment-banner" role="status">
                  Este cliente tiene {customerConsignmentSummary.count}{' '}
                  {customerConsignmentSummary.count === 1
                    ? 'consignación no cobrada'
                    : 'consignaciones no cobradas'}
                  : {formatMoney(customerConsignmentSummary.amount)}
                </div>
              )}
              <div className="admin-customers-page__edit-header">
                <button
                  type="button"
                  className="admin-customers-page__btn admin-customers-page__btn-secondary"
                  onClick={() => setEditExpanded((prev) => !prev)}
                  disabled={saving}
                >
                  {editExpanded ? 'Cerrar edición' : 'Editar'}
                </button>
              </div>
              {editExpanded ? (
                <>
                  <div className="admin-staff-page__fields admin-customers-page__fields">
                    <label>
                      Nombre
                      <input name="name" value={form.name} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      Email
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      Teléfono
                      <input name="phone" value={form.phone} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      CUIT / identificador
                      <input name="cuit" value={form.cuit} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      CUIL
                      <input name="cuil" value={form.cuil} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      DNI
                      <input name="dni" value={form.dni} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      Tipo doc. (AFIP)
                      <select
                        name="documentType"
                        value={form.documentType}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      >
                        <option value="80">CUIT</option>
                        <option value="86">CUIL</option>
                        <option value="96">DNI</option>
                        <option value="99">Sin identificar</option>
                      </select>
                    </label>
                    {isNestedCustomerSource(activeSource) && (
                      <label>
                        Condición IVA
                        <select
                          name="ivaCondition"
                          value={form.ivaCondition}
                          onChange={handleFormChange}
                          disabled={readOnly}
                        >
                          <option value="1">Responsable Inscripto</option>
                          <option value="3">Exento</option>
                          <option value="4">No Responsable</option>
                          <option value="5">Consumidor Final</option>
                          <option value="6">Responsable Monotributo</option>
                          <option value="8">No Categorizado</option>
                          <option value="9">Resp. Inscripto - Agente Percepción</option>
                          <option value="10">Resp. Inscripto - Agente Retención</option>
                        </select>
                      </label>
                    )}
                    <label className="admin-customers-page__field-span2">
                      Domicilio / dirección
                      <input name="address" value={form.address} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      Ciudad / localidad
                      <input name="city" value={form.city} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    <label>
                      Código postal
                      <input
                        name="postalCode"
                        value={form.postalCode}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      Provincia
                      <input
                        name="province"
                        value={form.province}
                        onChange={handleFormChange}
                        disabled={readOnly}
                      />
                    </label>
                    <label>
                      País
                      <input name="country" value={form.country} onChange={handleFormChange} disabled={readOnly} />
                    </label>
                    {isNestedCustomerSource(activeSource) ? (
                      <label className="admin-customers-page__field-span2">
                        Observaciones (gestión interna)
                        <textarea
                          name="observaciones"
                          value={form.observaciones}
                          onChange={handleFormChange}
                          disabled={readOnly}
                          rows={3}
                        />
                      </label>
                    ) : (
                      <label className="admin-customers-page__field-span2">
                        Información adicional (pedido)
                        <textarea
                          name="informacionAdicional"
                          value={form.informacionAdicional}
                          onChange={handleFormChange}
                          disabled={readOnly}
                          rows={3}
                        />
                      </label>
                    )}
                  </div>
                  <div className="admin-customers-page__actions">
                    <button
                      type="button"
                      className="admin-customers-page__btn admin-customers-page__btn-primary"
                      onClick={handleSave}
                      disabled={readOnly || saving}
                    >
                      {saving ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                    <button
                      type="button"
                      className="admin-customers-page__btn admin-customers-page__btn-secondary"
                      onClick={() => selectedRow && setForm(docToForm(selectedRow, activeSource))}
                      disabled={readOnly || saving}
                    >
                      Deshacer cambios
                    </button>
                  </div>
                </>
              ) : null}

              <div className="admin-customers-page__orders-block">
                <h3>{isConsignmentTab(activeSource) ? 'Consignaciones del cliente' : isQuoteSource(activeSource) ? 'Presupuestos del cliente' : 'Órdenes del cliente'}</h3>
                {loadingCustomerOrders ? (
                  <div className="admin-customers-page__spinner-wrap">
                    <Spinner />
                  </div>
                ) : customerOrders.length === 0 ? (
                  <p className="admin-customers-page__muted">
                    {isQuoteSource(activeSource)
                      ? 'No se encontraron más presupuestos para este cliente.'
                      : 'No se encontraron más órdenes para este cliente.'}
                  </p>
                ) : (
                  <ul className="admin-customers-page__orders-list">
                    {customerOrders.map((order) => {
                      const dt = toJsDate(order.date);
                      const statusMeta = getOrderStatusMeta(order, activeSource);
                      const paymentMethod =
                        isPhysicalSource(activeSource)
                          ? formatPaymentMethods(order.paymentMethods, '')
                          : order.paymentMethod || order.medioDePago || '';
                      const daysOpen = isConsignmentSale(order) ? daysSinceSale(order) : null;
                      const ageBucket =
                        daysOpen != null && isUnpaidSale(order) ? getAgingBucket(daysOpen) : null;
                      return (
                        <li key={order.id} className={`admin-customers-page__orders-item ${isQuoteSource(activeSource) ? 'is-quote' : ''}`}>
                          <div className="admin-customers-page__orders-main">
                            <span>#{order.orderNumber || '—'} · {dt ? dt.toLocaleString('es-AR') : '—'}</span>
                            <span className={`admin-customers-page__status-badge is-${statusMeta.tone}`}>
                              {statusMeta.label}
                            </span>
                            {daysOpen != null ? (
                              <span
                                className={`admin-customers-page__status-badge ${ageBucket ? `is-${ageBucket.id}` : 'is-ok'}`}
                              >
                                {isUnpaidSale(order) ? formatDaysOpen(daysOpen) : `Cobrada · ${formatDaysOpen(daysOpen)}`}
                              </span>
                            ) : null}
                            {paymentMethod ? (
                              <span className="admin-customers-page__orders-payment">Pago: {paymentMethod}</span>
                            ) : null}
                          </div>
                          <div className="admin-customers-page__orders-side">
                            <strong>{formatMoney(order.totalPrice)}</strong>
                            {isQuoteSource(activeSource) ? (
                              <div className="admin-customers-page__quote-item-actions">
                                <button
                                  type="button"
                                  className="admin-customers-page__btn admin-customers-page__btn-secondary admin-customers-page__btn-quote"
                                  onClick={() => setPreviewQuote(order)}
                                >
                                  Ver PDF
                                </button>
                                {!readOnly ? (
                                  <>
                                  <button
                                    type="button"
                                    className="admin-customers-page__btn admin-customers-page__btn-invoice admin-customers-page__btn-quote"
                                    onClick={() => handleSendQuoteEmail(order)}
                                    disabled={sendingQuoteId === order.id}
                                  >
                                    {sendingQuoteId === order.id ? 'Enviando…' : 'Enviar email'}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-customers-page__btn admin-customers-page__btn-danger admin-customers-page__btn-quote"
                                    onClick={() => handleDeleteQuote(order)}
                                    disabled={deletingQuoteId === order.id || sendingQuoteId === order.id}
                                  >
                                    {deletingQuoteId === order.id ? 'Eliminando…' : 'Eliminar'}
                                  </button>
                                  </>
                                ) : null}
                              </div>
                            ) : null}
                            {isPhysicalSource(activeSource) && isConsignmentSale(order) && !readOnly ? (
                              <>
                                <button
                                  type="button"
                                  className={`admin-customers-page__btn ${isUnpaidSale(order) ? 'admin-customers-page__btn-primary' : 'admin-customers-page__btn-secondary'} admin-customers-page__btn-paid`}
                                  onClick={() => handleToggleConsignmentPaid(order)}
                                  disabled={markingPaidId === order.id || issuingInvoiceId === order.id}
                                >
                                  {markingPaidId === order.id
                                    ? 'Guardando…'
                                    : isUnpaidSale(order)
                                      ? 'Marcar pagada'
                                      : 'Marcar no cobrada'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-customers-page__btn admin-customers-page__btn-invoice admin-customers-page__btn-paid"
                                  onClick={() => handleIssueConsignmentInvoice(order)}
                                  disabled={markingPaidId === order.id || issuingInvoiceId === order.id}
                                >
                                  {issuingInvoiceId === order.id
                                    ? (order.invoiceData ? 'Enviando…' : 'Emitiendo…')
                                    : order.invoiceData
                                      ? 'Enviar factura'
                                      : 'Emitir Factura'}
                                </button>
                              </>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {previewQuote ? (
        <div className="admin-customers-page__quote-modal" onClick={() => setPreviewQuote(null)} role="dialog" aria-modal="true">
          <div className="admin-customers-page__quote-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-customers-page__quote-modal-head">
              <h3>Presupuesto #{previewQuote.quoteNumber || previewQuote.orderNumber}</h3>
              <div className="admin-customers-page__quote-actions">
                {!readOnly ? (
                  <button
                    type="button"
                    className="admin-customers-page__btn admin-customers-page__btn-danger admin-customers-page__btn-quote"
                    onClick={() => handleDeleteQuote(previewQuote)}
                    disabled={deletingQuoteId === previewQuote.id}
                  >
                    {deletingQuoteId === previewQuote.id ? 'Eliminando…' : 'Eliminar'}
                  </button>
                ) : null}
                <button type="button" className="admin-customers-page__btn admin-customers-page__btn-secondary admin-customers-page__btn-quote" onClick={() => setPreviewQuote(null)}>
                  Cerrar
                </button>
              </div>
            </div>
            <InvoicePrinter
              saleData={previewQuote}
              invoiceData={buildQuoteInvoiceData(previewQuote)}
              title="Presupuesto"
              showPrintButton
              fullPage
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminCustomersPage;
